// Fewer "unavailable" cells: when a total is withheld (the API publishes a
// venue's flows only once 90% of value is measured), the UI can still say
// something true from the per-product figures.
//
// - A signed figure (net flow): a partial sum is not a bound in either
//   direction, so it shows as the sum with "partial" and its coverage.
// - Under half the value: nothing, with one short reason (which chain's
//   figures are pending).

import { chainLabel } from "./api";
import { coveragePercent } from "./dashboard";

/** A product's figure, its value (the coverage weight) and its chain, for the reason. */
export interface PartialItem {
  value: number | null | undefined;
  cap: number | null | undefined;
  chain?: string | null;
}

export type PartialFigure =
  | { kind: "exact"; value: number }
  | { kind: "partial"; value: number; share: number }
  | { kind: "none"; reason: string };

/** Half the value: below it a partial figure says too little to show. */
export const PARTIAL_MIN_SHARE = 0.5;

const finite = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v);

/**
 * The best true figure: the published total, else the measured products'
 * partial sum when they hold at least half the value, else a reason.
 */
export function partialFigure(total: number | null | undefined, items: readonly PartialItem[], what: string): PartialFigure {
  if (finite(total)) return { kind: "exact", value: total };
  let capAll = 0;
  let capMeasured = 0;
  let sum = 0;
  let measured = 0;
  const pendingByChain = new Map<string, number>();
  for (const item of items) {
    const cap = finite(item.cap) && item.cap > 0 ? item.cap : 0;
    capAll += cap;
    if (finite(item.value)) {
      sum += item.value;
      capMeasured += cap;
      measured += 1;
    } else if (item.chain) {
      pendingByChain.set(item.chain, (pendingByChain.get(item.chain) ?? 0) + cap);
    }
  }
  const share = capAll > 0 ? capMeasured / capAll : 0;
  if (measured > 0 && share >= PARTIAL_MIN_SHARE) return { kind: "partial", value: sum, share };
  // The chain holding the most unmeasured value names what is missing.
  const pending = [...pendingByChain].sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!measured) return { kind: "none", reason: pending ? `${chainLabel(pending)} ${what} pending` : `${what} pending` };
  return { kind: "none", reason: `${coveragePercent(share)} of value counted${pending ? `; ${chainLabel(pending)} pending` : ""}` };
}

/** The figure as text: "+$1.2M"; "—" when there is none. */
export function partialText(figure: PartialFigure, format: (v: number) => string): string {
  return figure.kind === "none" ? "—" : format(figure.value);
}

/** The line under a figure: "partial · 72% of value", or the reason. */
export function partialNote(figure: PartialFigure): string | null {
  switch (figure.kind) {
    case "exact":
      return null;
    case "partial":
      return `partial · ${coveragePercent(figure.share)} of value`;
    case "none":
      return figure.reason;
  }
}
